import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  reload,
  getAuth,
  signInAnonymously,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import Sentry from '../config/sentry';
import { logger } from '../services/logger';
import { subscribeToMedicationCatalog } from '../services/medicationCatalogService';
import { resetMedicationCatalogToFallback } from '../utils/medicationDatabase';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  sendVerification: () => Promise<void>;
  signInAnon: () => Promise<void>;
  logout: () => Promise<void>;
  forceLogout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<{ isNewUser: boolean } | void>;
  signInWithApple: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setIsInitialized] = useState(false);
  const log = useMemo(() => logger.createScope('AuthContext'), []);

  // Initialize auth monitoring
  useEffect(() => {
    log.debug('AuthProvider initializing, setting up auth state listener');
    
    let isResolved = false;
    
    // Set a timeout to prevent infinite loading (5 seconds max)
    const timeoutId = setTimeout(() => {
      if (!isResolved) {
        log.warn('Auth state listener timeout - proceeding without auth state');
        setLoading(false);
        setIsInitialized(true);
        isResolved = true;
      }
    }, 5000); // 5 second timeout - reduced to prevent long loading
    
    // Get the auth instance
    try {
      const auth = getAuth();
      
      // Add immediate check - if auth is already available, set loading to false quickly
      if (auth.currentUser !== null) {
        log.debug('Auth user already available, skipping wait');
        setCurrentUser(auth.currentUser);
        setLoading(false);
        setIsInitialized(true);
        if (!isResolved) {
          clearTimeout(timeoutId);
          isResolved = true;
        }
      }
      
      // Check for redirect result (if user was redirected back from OAuth)
      const checkRedirectResult = async () => {
        try {
          const { getRedirectResult } = await import('firebase/auth');
          const result = await getRedirectResult(auth);
          if (result) {
            const info = getAdditionalUserInfo(result);
            log.info('Google sign-in successful (redirect)', {
              userId: result.user.uid,
              email: result.user.email,
              isNewUser: !!info?.isNewUser
            });
          }
        } catch (error) {
          // Ignore redirect errors - user might not have come from a redirect
          log.debug('No redirect result or redirect error', { error: (error as Error).message });
        }
      };
      
      // Check for redirect result first (non-blocking)
      checkRedirectResult().catch(() => {
        // Silently fail - don't block auth initialization
      });
      
      // Set up auth state listener (persist user session across refresh)
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        // Clear timeout since we got a response
        if (!isResolved) {
          clearTimeout(timeoutId);
          isResolved = true;
        }
        
        log.debug('Auth state change detected', {
          authenticated: !!user,
          userId: user?.uid,
          isAnonymous: user?.isAnonymous
        });
        
        // Clear anonymous users to force proper authentication
        if (user && user.isAnonymous) {
          log.info('Clearing anonymous user to force proper authentication');
          signOut(auth);
          return;
        }
        
        // Update user state immediately to prevent race conditions
        setCurrentUser(user);
        setLoading(false);
        setIsInitialized(true);

        // Set Sentry user context for error attribution
        if (user) {
          Sentry.setUser({ id: user.uid, email: user.email || undefined });
        } else {
          Sentry.setUser(null);
        }

        // Log auth state change for debugging
        if (user) {
          log.info('User authenticated', {
            userId: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            providers: user.providerData.map(p => p.providerId)
          });
        } else {
          log.info('User signed out');
        }
      }, (error) => {
        // Handle auth errors
        if (!isResolved) {
          clearTimeout(timeoutId);
          isResolved = true;
        }
        log.error('Auth state listener error', error as Error);
        setLoading(false);
        setIsInitialized(true);
      });
      
      return () => {
        if (!isResolved) {
          clearTimeout(timeoutId);
        }
        unsubscribe();
      };
      
    } catch (error) {
      if (!isResolved) {
        clearTimeout(timeoutId);
        isResolved = true;
      }
      log.error('Error setting up auth state monitoring', error as Error);
      setLoading(false);
      setIsInitialized(true);
      return () => {};
    }
  }, [log]);

  // Merge Firestore reference medication catalog (web + iOS use same `medicationCatalog/v1` doc)
  useEffect(() => {
    if (!currentUser) {
      resetMedicationCatalogToFallback();
      return;
    }
    return subscribeToMedicationCatalog();
  }, [currentUser]);

  // Automatic anonymous sign-in disabled - users must authenticate properly
  // useEffect(() => {
  //   // Only run in development environment and when auth is initialized but no user is signed in
  //   const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  //   if (isInitialized && !loading && !currentUser && isLocalhost) {
  //     console.log('Development environment detected, attempting anonymous sign-in');
  //     signInAnon().catch(error => {
  //       console.warn('Auto anonymous sign-in failed:', error.message);
  //     });
  //   }
  // }, [isInitialized, loading, currentUser]);

  const signIn = async (email: string, password: string) => {
    try {
      const auth = getAuth();
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // If email not verified, redirect flow will handle; but ensure we notify callers
      if (!cred.user.emailVerified) {
        log.warn('Email not verified', { userId: cred.user.uid, email: cred.user.email });
        // Trigger a reload to ensure latest verification state if they just clicked
        try { await reload(cred.user); } catch {}
      }
      log.info('Sign in successful', { userId: cred.user.uid, email: cred.user.email });
    } catch (error) {
      log.error('Sign in error', error as Error, { email });
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const auth = getAuth();
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      log.info('Sign up successful', { userId: cred.user.uid, email: cred.user.email });
      // Verification email is now sent by the `onUserCreate` Auth trigger
      // (Phase 2.7) — no explicit `sendEmailVerification` call here. The
      // trigger uses our Workspace Gmail-API path so the user gets a
      // From: noreply@suprie.com instead of noreply@<project>.firebaseapp.com.
      // The "Resend" buttons on /verify-email + /trial use the
      // requestAuthEmail callable to re-trigger via the same path.
    } catch (error) {
      log.error('Sign up error', error as Error, { email });
      throw error;
    }
  };

  const sendVerification = async () => {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }
    // Phase 2.7 — route through our requestAuthEmail callable (which sends
    // via Workspace Gmail-API impersonating noreply@suprie.com) instead of
    // Firebase's built-in sendEmailVerification.
    const callable = httpsCallable<
      { type: 'verification' },
      { success: boolean; alreadyVerified?: boolean }
    >(functions, 'requestAuthEmail');
    await callable({ type: 'verification' });
  };

  const signInAnon = async () => {
    try {
      const auth = getAuth();
      await signInAnonymously(auth);
      log.info('Anonymous sign-in successful');
    } catch (error) {
      log.error('Anonymous sign-in error', error as Error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      await signOut(auth);
      setCurrentUser(null); // Clear user state immediately
      log.info('Logout successful', { userId });
      // Clear any cached data that might interfere with future logins
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear Firebase-specific cache to prevent auth/internal-error after logout
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('firebase:') || key.startsWith('firebaseui:')) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        // Ignore localStorage errors
      }
    } catch (error) {
      log.error('Logout error', error as Error);
      throw error;
    }
  };

  // Force logout function for development
  const forceLogout = async () => {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      await signOut(auth);
      setCurrentUser(null);
      localStorage.clear();
      sessionStorage.clear();
      log.info('Force logout successful', { userId });
    } catch (error) {
      log.error('Force logout error', error as Error);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Phase 2.7 — Workspace Gmail-API path (noreply@suprie.com) via the
      // requestAuthEmail callable. Always returns success to avoid leaking
      // account existence; rate-limited 5 / 15min per IP server-side.
      const callable = httpsCallable<
        { type: 'passwordReset'; email: string },
        { success: boolean }
      >(functions, 'requestAuthEmail');
      await callable({ type: 'passwordReset', email });
      log.info('Password reset email requested', { email });
    } catch (error) {
      log.error('Password reset error', error as Error, { email });
      throw error;
    }
  };

  // Social Authentication Methods
  const signInWithGoogle = async () => {
    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      // Set custom parameters to prevent popup from closing prematurely
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      // Try popup first (better UX), but fall back to redirect if it fails
      // Redirect is more reliable with strict CSP/COOP policies
      try {
        const result = await signInWithPopup(auth, provider);
        const info = getAdditionalUserInfo(result);
        
        // Wait a moment to ensure auth state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Verify the user is actually signed in
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('Authentication completed but user state not updated');
        }
        
        log.info('Google sign-in successful (popup)', {
          userId: result.user.uid,
          email: result.user.email,
          isNewUser: !!info?.isNewUser
        });
        
        return { isNewUser: !!info?.isNewUser };
      } catch (popupError: unknown) {
        // If popup fails for any reason, use redirect as fallback
        // This handles: blocked popups, COOP violations, user cancellation, etc.
        const popupCode = popupError instanceof Error && 'code' in popupError ? (popupError as Error & { code?: string }).code : undefined;
        const popupMessage = popupError instanceof Error ? popupError.message : String(popupError);
        const shouldFallbackToRedirect =
          popupCode === 'auth/popup-blocked' ||
          popupCode === 'auth/popup-closed-by-user' ||
          popupCode === 'auth/internal-error' ||
          popupCode === 'auth/cancelled-popup-request' ||
          popupMessage?.includes('Cross-Origin-Opener-Policy') ||
          popupMessage?.includes('COOP');

        if (shouldFallbackToRedirect) {
          log.warn('Popup sign-in failed, using redirect instead', {
            error: popupCode,
            message: popupMessage,
            scope: 'AuthContext'
          });
          
          // Use redirect as fallback (more reliable with strict security policies)
          const { signInWithRedirect } = await import('firebase/auth');
          await signInWithRedirect(auth, provider);
          // Note: signInWithRedirect doesn't return - it redirects the page
          // The auth state change will handle the result via getRedirectResult
          return;
        }
        // Re-throw other errors
        throw popupError;
      }
    } catch (error: unknown) {
      const code = error instanceof Error && 'code' in error ? (error as Error & { code?: string }).code : undefined;
      const message = error instanceof Error ? error.message : String(error);
      log.error('Google sign-in error', error as Error, {
        code,
        message
      });

      // Provide more helpful error messages
      if (code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        throw new Error('Sign-in was cancelled. Please try again.');
      } else if (code === 'auth/internal-error') {
        throw new Error('An internal error occurred. Please try refreshing the page and try again.');
      }

      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      const auth = getAuth();
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider);
      log.info('Apple sign-in successful', {
        userId: result.user.uid,
        email: result.user.email
      });
    } catch (error: unknown) {
      log.error('Apple sign-in error', error as Error);
      throw error;
    }
  };

  const signInWithFacebook = async () => {
    try {
      const auth = getAuth();
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      provider.addScope('public_profile');
      
      const result = await signInWithPopup(auth, provider);
      log.info('Facebook sign-in successful', {
        userId: result.user.uid,
        email: result.user.email
      });
    } catch (error: unknown) {
      log.error('Facebook sign-in error', error as Error);
      throw error;
    }
  };

  // Effect to ensure user document is created when user signs in
  useEffect(() => {
    if (!currentUser) return;
    const ensureUserDocument = async (user: FirebaseUser) => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            email: user.email,
            displayName: user.displayName || '',
            createdAt: new Date().toISOString(),
            onboardingComplete: false,
          });
          log.info('Created user document', { userId: user.uid, email: user.email });
        }
      } catch (error) {
        log.error('Error ensuring user document', error as Error, { userId: user.uid });
      }
    };
    ensureUserDocument(currentUser);
  }, [currentUser, log]);

  const value = {
    currentUser,
    loading,
    signIn,
    signUp,
    sendVerification,
    signInAnon,
    logout,
    forceLogout,
    resetPassword,
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 